import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBusinessOpen, setIsBusinessOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex-shrink-0">
            <img 
              src="https://static.readdy.ai/image/41be8099d52dd4fa30a2ec6c94e7a004/24fec1aa05007cbfcecf255bcf136873.png" 
              alt="Exosome One" 
              className="h-12 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isActive('/') ? 'text-teal-600' : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              ホーム
            </Link>
            <Link 
              to="/news" 
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isActive('/news') ? 'text-teal-600' : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              お知らせ
            </Link>
            <Link 
              to="/about" 
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isActive('/about') ? 'text-teal-600' : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              会社概要
            </Link>
            <div className="relative">
              <button 
                onClick={() => setIsBusinessOpen(!isBusinessOpen)}
                className={`text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer ${isActive('/business') ? 'text-teal-600' : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
              >
                事業内容
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              {isBusinessOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2">
                  <Link to="/business/support" className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 cursor-pointer">再生医療事業への支援</Link>
                  <Link to="/business/r-d" className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 cursor-pointer">研究・開発</Link>
                  <Link to="/business/ingredients" className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 cursor-pointer">化粧品原料の開発・製造・販売</Link>
                  <Link to="/business/bringup" className="block px-4 py-2 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 cursor-pointer">細胞の培養</Link>
                </div>
              )}
            </div>
            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isActive('/contact') ? 'text-teal-600' : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              お問い合わせ
            </Link>
          </nav>

          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white shadow-lg">
          <nav className="px-4 py-4 space-y-3">
            <Link to="/" className="block py-2 text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>ホーム</Link>
            <Link to="/news" className="block py-2 text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>お知らせ</Link>
            <Link to="/about" className="block py-2 text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>会社概要</Link>
            <div>
              <button 
                onClick={() => setIsBusinessOpen(!isBusinessOpen)}
                className="w-full text-left py-2 text-sm font-medium text-gray-700 hover:text-teal-600 flex items-center justify-between cursor-pointer"
              >
                事業内容
                <i className={`ri-arrow-${isBusinessOpen ? 'up' : 'down'}-s-line text-base`}></i>
              </button>
              {isBusinessOpen && (
                <div className="pl-4 space-y-2 mt-2">
                  <Link to="/business/support" className="block py-2 text-sm text-gray-600 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>再生医療事業への支援</Link>
                  <Link to="/business/r-d" className="block py-2 text-sm text-gray-600 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>研究・開発</Link>
                  <Link to="/business/ingredients" className="block py-2 text-sm text-gray-600 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>化粧品原料の開発・製造・販売</Link>
                  <Link to="/business/bringup" className="block py-2 text-sm text-gray-600 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>細胞の培養</Link>
                </div>
              )}
            </div>
            <Link to="/contact" className="block py-2 text-sm font-medium text-gray-700 hover:text-teal-600 cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>お問い合わせ</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
